/* eslint-disable no-underscore-dangle */
import React, { useRef, useState, useEffect } from 'react';
import { RangePicker } from '@brokerbay/design-components';
import moment from 'moment';

export type DateRangePickerProps = React.ComponentPropsWithoutRef<
  typeof RangePicker
> & {
  value?: [start: moment.Moment, end: moment.Moment];
  disableFutureDates?: boolean;
};

const DATE_FORMAT = 'MMM DD YYYY';

const DateRangePicker = (props: DateRangePickerProps) => {
  const { value = [], onChange, disableFutureDates } = props;

  const containerRef = useRef<HTMLDivElement>(null);

  const [start, setStart] = useState<moment.Moment | undefined>(undefined);
  const [end, setEnd] = useState<moment.Moment | undefined>(undefined);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  /** Prevent closing when only one date has changed */
  const keepOpen = useRef(false);

  // Update internal state on value prop change
  useEffect(() => {
    const [_start, _end] = value || [];
    if (!_start?.isSame(start)) {
      setStart(_start);
    }
    if (!_end?.isSame(end)) {
      setEnd(_end);
    }
  }, [value]);

  // Only pass internal state change to parent if either both dates are defined
  // or both dates are empty (input cleared)
  useEffect(() => {
    if (Boolean(start) === Boolean(end)) {
      onChange([start, end]);
      keepOpen.current = false;
      setIsOpen(false);
    }
  }, [start, end]);

  // Is fired after onChange or when clicked off
  const _onOpenChange = (open: boolean) => {
    if (open) setIsOpen(true);
    else if (keepOpen.current) keepOpen.current = false;
    else setIsOpen(false);
  };

  const _onChange = (val?: [start?: moment.Moment, end?: moment.Moment]) => {
    // When input is cleared, onChange is called with null instead of array
    if (!val) {
      setStart(undefined);
      setEnd(undefined);
      keepOpen.current = false;
    } else {
      const [_start, _end] = val;
      setStart(_start);
      setEnd(_end);
      // Make sure onOpenChange doesn't prematurely close picker when it gets fired afterwards
      keepOpen.current = !_start || !_end;
    }
  };

  return (
    <div ref={containerRef}>
      <RangePicker
        value={[start, end]}
        allowEmpty={[true, true]}
        allowClear
        format={DATE_FORMAT}
        picker="date"
        {...props}
        open={isOpen || keepOpen.current}
        onOpenChange={_onOpenChange}
        onChange={_onChange}
        data-cy="showings-calendar-date-picker"
        disabledDate={(current: moment.Moment) =>
          disableFutureDates && current.isAfter(moment())
        }
      />
    </div>
  );
};

export default DateRangePicker;
